/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useCallback, useRef } from "react"
import type { ChangeEvent, FC, ReactElement, RefObject } from "react"

import eq from "lodash-es/eq"
import isNull from "lodash-es/isNull"
import isString from "lodash-es/isString"
import isUndefined from "lodash-es/isUndefined"
import trim from "lodash-es/trim"
import { type UseFormReturn, useForm } from "react-hook-form"
import { useBoolean, useMount, useUnmount, useUpdateEffect } from "react-use"

import {
    CompletionMark,
    ESpecialChar,
    ETextAreaBorderWidth,
    ETextAreaVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    SpecialChars,
    TextArea,
    Typography
} from "@/3514/components"
import { type TUseInteractionState, useInteractionState } from "@/3514/hooks"
import { type IUseTranslation, useTranslation } from "@/hooks"
import type { TEmptyCallback } from "@/shared/types/functions"

import type {
    TCoachingChatSeedMutationParams as TMutationParams,
    TCoachingChatSeedMutation as TSeedAnswerMutation
} from "../api"
import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatComponentsGuards as guards } from "../guards"
// deep import on purpose: the hooks barrel drags the whole chat runtime in
import {
    type TCoachingChatSeedDrafts as TSeedDrafts,
    useCoachingChatSeedDrafts as useSeedDrafts
} from "../hooks/coaching-chat.seed-drafts.hook"
import {
    useCoachingChatFormDispatcher as useFormDispatcher,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatSeedQuestionFormField as EFormField,
    type TCoachingChatSeedQuestionFormField as TFormField
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"
import { coachingChatSeedQuestionFormResolver as formResolver } from "../validation"

type TProps = {
    index: number
}

const { withModeQuestion: withGuard } = guards

const {
    formExtractors: { getSeedAnswerMutationErrorMessage: getErrorMessage }
} = uiUtils

const {
    form: { seedQuestion: fieldClasses }
} = chatStyles

const FullQuestion: FC<TProps> = ({ index }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        entityId,
        chapterId,
        form: { mutationHook, mutationOptionsHook, isRequired: isFieldRequired }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const {
        onError: onMutationError,
        onMutate: onMutationStart,
        onSuccess: onMutationSuccess
    }: ReturnType<typeof mutationOptionsHook> = mutationOptionsHook(entityId)

    const {
        mutateAsync: sendAnswer,
        status: mutationStatus,
        error: mutationError,
        reset: resetMutation,
        variables: mutationVariables
    }: TSeedAnswerMutation = mutationHook({
        chapterId,
        chapterComponentId: entityId,
        onMutate: onMutationStart,
        onSuccess: onMutationSuccess,
        onError: onMutationError
    })

    useUnmount((): void => {
        resetMutation()
    })

    const formDispatcher: ReturnType<typeof useFormDispatcher> = useFormDispatcher(entityId)

    const { questions, isSubmitted: areSeedsSubmitted }: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const question: TFormField = questions[index]

    const initialQuestionAnswer: RefObject<string> = useRef<string>(question.answer)

    const isMutationError: boolean = isUndefined(mutationVariables)
        ? false
        : eq(mutationVariables.question_uuid, question.id)
          ? eq(mutationStatus, "error")
          : false
    const isMutationLoading: boolean = isUndefined(mutationVariables)
        ? false
        : eq(mutationVariables.question_uuid, question.id)
          ? eq(mutationStatus, "pending")
          : false

    const mutationAnswer: string | undefined = isUndefined(mutationVariables)
        ? undefined
        : eq(mutationVariables.question_uuid, question.id)
          ? mutationVariables.answer
          : String("")

    const {
        setValue: setFieldValue,
        reset: resetForm,
        handleSubmit
    }: UseFormReturn<TFormField> = useForm({
        reValidateMode: "onBlur",
        mode: "onBlur",
        resolver: formResolver,
        defaultValues: question
    })

    const textAreaRef: RefObject<HTMLTextAreaElement> = useRef<HTMLTextAreaElement>(null)

    const seedDrafts: TSeedDrafts = useSeedDrafts(entityId)

    // Answers only reach the server on blur, so whatever was typed since the last blur exists
    // nowhere but the DOM. Restore it into the field (and the form state) without touching the
    // store: `question.answer` stays server truth, and the first blur promotes the draft through
    // the normal mutation, because handleTextAreaBlur already compares the two.
    useMount((): void => {
        // locked seeds are read-only; CoachingChatQuestions owns dropping the drafts in that case
        if (areSeedsSubmitted) return void 0

        const draft: string | null = seedDrafts.read(question.id)

        if (isNull(draft) || eq(draft, question.answer)) return void 0

        if (!isNull(textAreaRef.current)) textAreaRef.current.value = draft

        setFieldValue(EFormField.Answer, draft, { shouldTouch: true, shouldValidate: true })

        return void 0
    })

    const [isTextAreaActive, setIsTextAreaActive]: ReturnType<typeof useBoolean> = useBoolean(false)

    useUpdateEffect((): void => (formDispatcher.setFormFilling(isTextAreaActive), void 0), [isTextAreaActive])

    const {
        actions: { onMouseEnter: onFormMouseEnter, onMouseLeave: onFormMouseLeave },
        isActionAffected: isFormActionAffected
    }: TUseInteractionState = useInteractionState()

    const handleTextAreaChange: (e: ChangeEvent<HTMLTextAreaElement>) => Promise<void> = useCallback(
        async (e: ChangeEvent<HTMLTextAreaElement>): Promise<void> => (
            !isUndefined(mutationError) && resetMutation(),
            setFieldValue(EFormField.Answer, e.target.value, {
                shouldValidate: true,
                shouldTouch: true
            }),
            (textAreaRef.current.value = e.target.value),
            seedDrafts.persist(question.id, e.target.value),
            void 0
        ),
        [setFieldValue, mutationError, resetMutation, seedDrafts, question.id]
    )

    const handleFormSubmit: TEmptyCallback = handleSubmit(
        async ({ id: question_uuid, answer }: TFormField): Promise<void> =>
            sendAnswer(
                { question_uuid, answer },
                {
                    // the server now holds this answer, so the draft has nothing left to protect
                    onSuccess: async (): Promise<void> => (
                        seedDrafts.drop(question_uuid), setIsTextAreaActive(false), textAreaRef.current?.blur()
                    ),
                    onError: async (_error: never, vars: TMutationParams): Promise<void> => (
                        resetForm({
                            [EFormField.Answer]: vars.answer,
                            [EFormField.Question]: question.question,
                            [EFormField.Id]: vars.question_uuid,
                            [EFormField.Index]: index
                        }),
                        isNull(textAreaRef.current)
                            ? void 0
                            : (textAreaRef.current.value = isString(vars.answer) ? vars.answer : String("")),
                        void 0
                    )
                }
            ).then()
    )

    const handleTextAreaFocus: TEmptyCallback = useCallback(
        (): void => (!areSeedsSubmitted ? (setIsTextAreaActive(true), textAreaRef?.current?.focus()) : void 0, void 0),
        [areSeedsSubmitted]
    )

    const handleTextAreaBlur: TEmptyCallback = useCallback(
        (): void => (
            !areSeedsSubmitted &&
            (isString(mutationAnswer)
                ? !eq(textAreaRef?.current?.value, mutationAnswer)
                : !eq(textAreaRef.current?.value, question.answer))
                ? handleFormSubmit()
                : void 0,
            setIsTextAreaActive(false),
            void 0
        ),
        [handleFormSubmit, areSeedsSubmitted, mutationAnswer, question.answer]
    )

    return (
        <form
            className={fieldClasses.form({
                isError: Boolean(isMutationLoading ? false : isMutationError),
                isActive: Boolean(isTextAreaActive ? true : isFormActionAffected),
                isLoading: isMutationLoading
            })}
            {...(!areSeedsSubmitted && { onMouseEnter: onFormMouseEnter, onMouseLeave: onFormMouseLeave })}
        >
            <Typography
                text={
                    <Fragment>
                        {question.question}
                        {SpecialChars[ESpecialChar.EmptySpace]}
                        <Typography
                            tag={ETypographyTag.Span}
                            text={getErrorMessage(isMutationError, mutationError, question.id, t)}
                            color={
                                Boolean(trim(question.answer).length) && !isMutationError
                                    ? ETypographyColor.Accent
                                    : ETypographyColor.Error
                            }
                            size={ETypographySize.Small}
                            weight={ETypographyWeight.Bold}
                        />
                    </Fragment>
                }
                color={ETypographyColor.Black}
                size={ETypographySize.Small}
                weight={ETypographyWeight.Bold}
                alignment={ETypographyAlignment.Left}
                isPretty
                className={fieldClasses.title({
                    isInactive: !isTextAreaActive,
                    isError: isMutationLoading ? false : isMutationError
                })}
            />

            <TextArea
                ref={textAreaRef}
                className={fieldClasses.textarea({
                    isError: isMutationError
                })}
                variant={
                    isTextAreaActive
                        ? isMutationError
                            ? ETextAreaVariant.Error
                            : ETextAreaVariant.Default
                        : isFormActionAffected
                          ? isMutationError
                              ? ETextAreaVariant.Error
                              : ETextAreaVariant.Default
                          : ETextAreaVariant.Transparent
                }
                placeholder={t("entities.coachingChat.chat.question.placeholder")}
                placeholderTextSize={ETypographySize.Medium}
                name={`${EFormField.Answer}-${question.id}`}
                defaultValue={question.answer || String()}
                isDisabled={Boolean(areSeedsSubmitted || isMutationLoading)}
                textColor={ETypographyColor.Dark}
                textSize={ETypographySize.Small}
                borderWidth={ETextAreaBorderWidth.Tiny}
                onChange={handleTextAreaChange}
                onFocus={handleTextAreaFocus}
                onBlur={handleTextAreaBlur}
                withItalicPlaceholder
                withNoResize
                minLines={3}
                maxLines={3}
                withSpellingCssOverride
                enterKeyHint="done"
            />

            <CompletionMark
                isRequired={Boolean(initialQuestionAnswer.current.length) ? false : isFieldRequired}
                isSaving={isMutationLoading}
                isCompleted={Boolean(trim(question.answer).length) && !isMutationLoading && !isMutationError}
            />
        </form>
    )
}

const LightQuestion: FC<TProps> = ({ index }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        entityId,
        form: { isRequired: isFieldRequired }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const { questions }: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const question: TFormField = questions[index]

    return (
        <form
            className={fieldClasses.form({
                isError: false,
                isActive: false,
                isLoading: false
            })}
        >
            <Typography
                text={
                    <Fragment>
                        {question.question}
                        {SpecialChars[ESpecialChar.EmptySpace]}
                        <Typography
                            tag={ETypographyTag.Span}
                            text={SpecialChars[ESpecialChar.Asterisk]}
                            color={
                                Boolean(trim(question.answer).length) ? ETypographyColor.Accent : ETypographyColor.Error
                            }
                            size={ETypographySize.Small}
                            weight={ETypographyWeight.Bold}
                        />
                    </Fragment>
                }
                color={ETypographyColor.Black}
                size={ETypographySize.Small}
                weight={ETypographyWeight.Bold}
                alignment={ETypographyAlignment.Left}
                className={fieldClasses.title({
                    isInactive: true,
                    isError: false
                })}
            />

            <TextArea
                className={fieldClasses.textarea({
                    isError: false
                })}
                variant={ETextAreaVariant.Transparent}
                placeholder={t("entities.coachingChat.form.placeholder")}
                placeholderTextSize={ETypographySize.Medium}
                name={`${EFormField.Answer}-${question.id}`}
                defaultValue={question.answer || String()}
                textColor={ETypographyColor.Dark}
                textSize={ETypographySize.Small}
                borderWidth={ETextAreaBorderWidth.Tiny}
                borderRadius={{ tl: 8, tr: 8, bl: 8, br: 8 }}
                withItalicPlaceholder
                isDisabled
                withNoResize
                minLines={3}
                maxLines={3}
            />

            <CompletionMark isRequired={isFieldRequired} isCompleted={Boolean(trim(question.answer).length)} />
        </form>
    )
}

const GuardedQuestion: FC<TProps> = withGuard<TProps>(FullQuestion, LightQuestion)

GuardedQuestion.displayName = "CoachingChatQuestion"

export { GuardedQuestion as CoachingChatQuestion }
